import { useState } from 'react';
import { Download, Pencil, Sheet, TriangleAlert, Upload, type LucideIcon } from 'lucide-react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import type {
  MovementSourceType,
  NegativeLineDTO,
  OperationListItemDTO,
  OperationStatus,
  OperationType,
} from '@lightbm/shared';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Checkbox } from '@/components/ui/Toggle';
import { useQuantity } from '@/features/products/parts';
import { cn } from '@/lib/cn';

const STATUS_TONES: Record<OperationStatus, BadgeTone> = {
  draft: 'draft',
  pending_approval: 'info',
  validated: 'success',
  cancelled: 'neutral',
  reversed: 'reversed',
};

export function OperationStatusBadge({ status }: { status: OperationStatus }) {
  const { t } = useTranslation('operations');
  return (
    <Badge tone={STATUS_TONES[status]} className={cn(status === 'cancelled' && 'line-through')}>
      {t(`status.${status}`)}
    </Badge>
  );
}

export const TYPE_ICONS: Record<OperationType, { icon: LucideIcon; tile: string }> = {
  stockIn: { icon: Download, tile: 'bg-primary-soft text-primary' },
  stockOut: { icon: Upload, tile: 'bg-warning-soft text-warning' },
  adjustment: { icon: Pencil, tile: 'bg-neutral-soft text-ink-2' },
  openingStock: { icon: Sheet, tile: 'bg-info-soft text-info' },
};

export function TypeIcon({ type, className }: { type: OperationType; className?: string }) {
  const { icon: Icon, tile } = TYPE_ICONS[type];
  return (
    <span
      className={cn(
        'flex size-11 shrink-0 items-center justify-center rounded-control',
        tile,
        className,
      )}
    >
      <Icon className="size-5" strokeWidth={1.9} />
    </span>
  );
}

/** The page of a document: an operation, a transfer, a count or an order. */
export const documentPath = (type: MovementSourceType | 'count', id: string) =>
  type === 'transfer'
    ? `/transfers/${id}`
    : type === 'count'
      ? `/counts/${id}`
      : type === 'order'
        ? `/orders/${id}`
        : `/operations/${id}`;

/** "Stock out · Sale", "Adjustment · Damaged", "Stock in · Receipt". */
export function useOperationLabels() {
  const { t } = useTranslation('operations');
  return {
    type: (type: MovementSourceType | 'count') => t(`types.${type}`),
    detail: (op: Pick<OperationListItemDTO, 'type' | 'outType' | 'reason'>) =>
      op.type === 'stockOut' && op.outType
        ? t(`outTypes.${op.outType}`)
        : op.type === 'adjustment'
          ? (op.reason?.name ?? null)
          : t(`subtypes.${op.type}`),
  };
}

/** "Main Depot › Zone A"; the system location reads "In transit" in the interface language. */
export const placeName = (
  location: { name: string; parentName: string | null; isSystem?: boolean } | null | undefined,
) =>
  location
    ? location.isSystem
      ? i18n.t('common:locationTypes.transit')
      : location.parentName
        ? `${location.parentName} › ${location.name}`
        : location.name
    : null;

/**
 * Rule 5.4 (design "Issue stock + negative stock check"): what goes below zero, an explicit
 * confirmation, and only for people allowed to confirm.
 */
export function NegativeStockDialog({
  open,
  lines,
  canConfirm,
  blocked,
  loading,
  onClose,
  onConfirm,
}: {
  open: boolean;
  lines: NegativeLineDTO[];
  canConfirm: boolean;
  blocked: boolean;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation('operations');
  const quantity = useQuantity();
  const [checked, setChecked] = useState(false);
  const close = () => {
    setChecked(false);
    onClose();
  };
  const sites = [...new Set(lines.map((l) => placeName(l.location)))].join(', ');

  return (
    <Dialog
      open={open}
      onClose={close}
      title={
        <span className="flex items-center gap-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-control bg-danger-soft text-danger">
            <TriangleAlert className="size-5" />
          </span>
          {t('negative.title', { count: lines.length })}
        </span>
      }
      description={
        blocked
          ? t('negative.blocked')
          : canConfirm
            ? t('negative.text', { places: sites })
            : t('negative.askManager')
      }
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            {t('negative.goBack')}
          </Button>
          {!blocked && canConfirm && (
            <Button
              variant="danger"
              disabled={!checked}
              loading={loading}
              onClick={() => {
                setChecked(false);
                onConfirm();
              }}
            >
              {t('negative.confirm')}
            </Button>
          )}
        </>
      }
    >
      <div className="overflow-x-auto rounded-control border border-line">
        <table className="w-full text-left">
          <thead className="bg-ground/70 text-sm text-ink-2">
            <tr>
              <th className="px-4 py-2.5 font-medium">{t('negative.productLocation')}</th>
              <th className="px-4 py-2.5 text-right font-medium">{t('negative.now')}</th>
              <th className="px-4 py-2.5 text-right font-medium">{t('negative.change')}</th>
              <th className="px-4 py-2.5 text-right font-medium">{t('negative.after')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={`${line.product.id}-${line.location.id}`} className="border-t border-line">
                <td className="px-4 py-3">
                  <span className="block font-semibold">{line.product.name}</span>
                  <span className="block text-sm text-muted">{placeName(line.location)}</span>
                </td>
                <td className="tabular px-4 py-3 text-right">
                  {quantity(line.now, line.product.unit)}
                </td>
                <td className="tabular px-4 py-3 text-right">
                  {quantity(Math.abs(line.change), line.product.unit)}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="tabular rounded-[6px] bg-danger-soft px-2 py-0.5 font-semibold text-danger">
                    {quantity(line.after, line.product.unit)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!blocked && canConfirm && (
        <label className="mt-4 flex cursor-pointer gap-3 rounded-control bg-ground/70 px-4 py-3.5">
          <Checkbox
            checked={checked}
            onChange={(event) => setChecked(event.target.checked)}
            className="mt-1"
          />
          <span className="text-ink-2">{t('negative.checkbox')}</span>
        </label>
      )}
      <p className="mt-4 text-sm text-muted">{t('negative.footnote')}</p>
    </Dialog>
  );
}
